import React from "react";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { DASHBOARD_FONT, DASHBOARD_NAVY, DASHBOARD_TEXT } from "../manage/dashboardUi";

export const TEAM_FORMATION_FONT = DASHBOARD_FONT;
export const TEAM_PAGE_BG = "#F4F6F8";
export const TEAM_TEXT = "#1A1A1A";
export const TEAM_MUTED = "#6B7280";
export const TEAM_LINK = DASHBOARD_NAVY;
export const TEAM_PRIMARY = DASHBOARD_NAVY;
export const TEAM_BORDER = "#DEE2E6";
export const TEAM_INFO_BG = "#E5F6FD";
export const TEAM_INFO_BORDER = "#B6E0FE";
export const TEAM_INFO_TEXT = "#607785";
export const TEAM_INFO_ICON = "#0288D1";

export const teamSectionPaperSx = {
  p: 2,
  borderRadius: "10px",
  bgcolor: "#FFFFFF",
  border: `1px solid ${TEAM_BORDER}`,
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
  fontFamily: TEAM_FORMATION_FONT,
} as const;

export const teamOverlineSx = {
  display: "block",
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: DASHBOARD_NAVY,
  lineHeight: 1.4,
  fontFamily: TEAM_FORMATION_FONT,
};

export const teamPanelHeadingSx = {
  fontFamily: TEAM_FORMATION_FONT,
  fontSize: "1.0625rem",
  fontWeight: 700,
  color: DASHBOARD_TEXT,
  lineHeight: 1.3,
};

export const teamPanelSubtextSx = {
  fontFamily: TEAM_FORMATION_FONT,
  fontSize: "0.8125rem",
  fontWeight: 400,
  color: TEAM_MUTED,
  lineHeight: 1.45,
};

export const teamSectionTitleSx = {
  fontSize: "1.125rem",
  fontWeight: 700,
  color: TEAM_TEXT,
  lineHeight: 1.3,
  fontFamily: TEAM_FORMATION_FONT,
};

export const teamBodySx = {
  fontSize: "0.8125rem",
  color: TEAM_MUTED,
  lineHeight: 1.45,
  fontFamily: TEAM_FORMATION_FONT,
};

export const teamStatBoxSx = {
  p: 1.25,
  borderRadius: "8px",
  border: `1px solid ${TEAM_BORDER}`,
  bgcolor: "#FFFFFF",
  fontFamily: TEAM_FORMATION_FONT,
} as const;

export const teamConsiderationChipSx = {
  fontFamily: TEAM_FORMATION_FONT,
  fontWeight: 600,
  fontSize: "0.8125rem",
  height: 32,
  borderRadius: "999px",
  bgcolor: TEAM_PRIMARY,
  color: "#FFFFFF",
  "& .MuiChip-label": { px: 1.5 },
};

export const teamApprovedChipSx = {
  fontFamily: TEAM_FORMATION_FONT,
  fontWeight: 600,
  fontSize: "0.8125rem",
  height: 32,
  borderRadius: "999px",
  bgcolor: "#FFFFFF",
  color: "#15803D",
  border: "1px solid #86EFAC",
  "& .MuiChip-label": { px: 1.5 },
};

export const teamCandidateTypeBadgeSx = (firstTime: boolean) => ({
  fontFamily: TEAM_FORMATION_FONT,
  fontSize: "0.75rem",
  fontWeight: 600,
  height: 24,
  borderRadius: "999px",
  bgcolor: "#FFFFFF",
  color: firstTime ? DASHBOARD_NAVY : TEAM_TEXT,
  border: `1px solid ${firstTime ? DASHBOARD_NAVY : TEAM_BORDER}`,
  "& .MuiChip-label": { px: 1 },
});

export const teamCandidateStatusBadgeSx = (status: string) => {
  if (status === "APPROVED_PROSPECT") {
    return {
      fontFamily: TEAM_FORMATION_FONT,
      fontSize: "0.75rem",
      fontWeight: 600,
      height: 24,
      borderRadius: "999px",
      bgcolor: "#FFFFFF",
      color: "#15803D",
      border: "1px solid #86EFAC",
      "& .MuiChip-label": { px: 1 },
    };
  }
  if (status === "CONCERN") {
    return {
      fontFamily: TEAM_FORMATION_FONT,
      fontSize: "0.75rem",
      fontWeight: 600,
      height: 24,
      borderRadius: "999px",
      bgcolor: "#FFFBEB",
      color: "#92400E",
      border: "1px solid #FCD34D",
      "& .MuiChip-label": { px: 1 },
    };
  }
  return {
    fontFamily: TEAM_FORMATION_FONT,
    fontSize: "0.75rem",
    fontWeight: 600,
    height: 24,
    borderRadius: "999px",
    bgcolor: "#F3F4F6",
    color: TEAM_TEXT,
    border: `1px solid ${TEAM_BORDER}`,
    "& .MuiChip-label": { px: 1 },
  };
};

export const teamCandidateBadgeSx = {
  fontFamily: TEAM_FORMATION_FONT,
  fontSize: "0.75rem",
  fontWeight: 600,
  height: 24,
  borderRadius: "999px",
  bgcolor: "#FFFFFF",
  color: TEAM_TEXT,
  border: `1px solid ${TEAM_BORDER}`,
  "& .MuiChip-label": { px: 1 },
};

export const teamRecipientChipSx = {
  fontFamily: TEAM_FORMATION_FONT,
  fontSize: "0.75rem",
  fontWeight: 400,
  height: "auto",
  borderRadius: "999px",
  bgcolor: "#FFFFFF",
  color: DASHBOARD_TEXT,
  border: `1px solid ${TEAM_BORDER}`,
  "& .MuiChip-label": {
    px: 1.25,
    py: 0.375,
    lineHeight: 1.35,
    whiteSpace: "normal",
  },
};

export const teamPrepareButtonSx = {
  fontFamily: TEAM_FORMATION_FONT,
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 1.75,
  py: 0.875,
  boxShadow: "none",
};

export function TeamFormationShell({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        fontFamily: `${TEAM_FORMATION_FONT} !important`,
        color: TEAM_TEXT,
        "&, & *": {
          fontFamily: `${TEAM_FORMATION_FONT} !important`,
        },
        "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiChip-label, & .MuiAlert-root, & .MuiFormControlLabel-label":
          {
            fontFamily: `${TEAM_FORMATION_FONT} !important`,
          },
      }}
    >
      {children}
    </Box>
  );
}

export function TeamSectionPaper({
  children,
  component,
}: {
  children: React.ReactNode;
  component?: React.ElementType;
}) {
  return (
    <Paper component={component} variant="outlined" sx={teamSectionPaperSx}>
      {children}
    </Paper>
  );
}

export function TeamOverline({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="span" sx={teamOverlineSx}>
      {children}
    </Typography>
  );
}

export function TeamInfoAlert({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: object;
}) {
  return (
    <Alert
      severity="info"
      sx={{
        mt: 1.5,
        ...sx,
        display: "flex",
        alignItems: "center",
        bgcolor: TEAM_INFO_BG,
        color: TEAM_INFO_TEXT,
        border: `1px solid ${TEAM_INFO_BORDER}`,
        borderRadius: "8px",
        fontSize: "0.875rem",
        lineHeight: 1.55,
        py: 0.875,
        px: 1.5,
        "& .MuiAlert-icon": {
          color: TEAM_INFO_ICON,
          opacity: 1,
          alignSelf: "center",
          mr: 1.25,
          p: 0,
          fontSize: "1.25rem",
        },
        "& .MuiAlert-message": {
          p: 0,
          color: TEAM_INFO_TEXT,
          lineHeight: 1.55,
        },
      }}
    >
      {children}
    </Alert>
  );
}
